import { useState, useEffect, useCallback } from 'preact/hooks'
import type { Ref } from 'preact'
import { Play, Loader2, AlertCircle } from 'lucide-preact'
import { touchPointsApi } from '@/api/touchPoints'

interface Props {
  workspaceId: string
  touchPointId: string
  /**
   * Fetch the recording immediately on mount (the read-only view panel) instead of waiting for a Play
   * click. The player is revealed loaded-but-paused — ready to watch, no autoplay — so opening the view
   * never blasts audio.
   */
  autoLoad?: boolean
  /** Forwarded to the <video> so a parent can drive playback (seek) for transcript sync (ORB-436). */
  videoRef?: Ref<HTMLVideoElement>
  /** Fires on the <video>'s timeupdate with the current playback position in seconds (ORB-436). */
  onTimeUpdate?: (currentTimeSeconds: number) => void
}

type PlayerState = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Inline playback for a meeting's Webster recording. The SAS URL is short-lived, so we fetch it on
 * demand — on the user's Play click, or on mount when autoLoad is set (the view panel) — and never hold
 * it longer than a viewing session. Stays inline in the panel rather than opening a modal.
 */
export function MeetingRecordingPlayer({ workspaceId, touchPointId, autoLoad = false, videoRef, onTimeUpdate }: Props) {
  const [state, setState] = useState<PlayerState>('idle')
  const [url, setUrl] = useState('')

  // Kept in a callback so the auto-load effect has a stable dependency.
  const load = useCallback(async () => {
    setState('loading')
    const res = await touchPointsApi.getRecording(workspaceId, touchPointId)
    if (res.data?.Url) {
      setUrl(res.data.Url)
      setState('ready')
    } else {
      setState('error')
    }
  }, [workspaceId, touchPointId])

  // View panel: fetch the SAS + reveal the player as soon as the panel opens. Guard on idle so a
  // re-render never re-fetches a fresh (short-lived) URL mid-view.
  useEffect(() => {
    if (autoLoad && state === 'idle') void load()
  }, [autoLoad, state, load])

  if (state === 'ready') {
    return (
      <video
        ref={videoRef}
        src={url}
        controls
        // Manual Play means "start it"; auto-load means "have it ready": loaded but paused.
        autoPlay={!autoLoad}
        preload="metadata"
        onTimeUpdate={onTimeUpdate ? (e: Event) => onTimeUpdate((e.currentTarget as HTMLVideoElement).currentTime) : undefined}
        class="mt-1 w-full aspect-video object-contain rounded-lg border border-slate-200 dark:border-slate-700 bg-black"
      />
    )
  }

  if (state === 'error') {
    return (
      <div class="inline-flex items-center gap-2 text-[12.5px] text-slate-500 dark:text-slate-400">
        <AlertCircle class="w-3.5 h-3.5" />
        <span>Couldn’t load the recording. It may not be ready yet.</span>
        <button type="button" onClick={load} class="font-medium text-primary-600 dark:text-primary-400 hover:underline">
          Try again
        </button>
      </div>
    )
  }

  // Auto-load fetches without a click, so reserve the player's space with a skeleton (not a spinner in
  // the middle of content); it also prevents a layout shift when the video pops in.
  if (autoLoad && state === 'loading') {
    return (
      <div
        role="status"
        aria-label="Loading recording"
        class="mt-1 w-full aspect-video rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700/40 motion-safe:animate-pulse"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={load}
      disabled={state === 'loading'}
      class="inline-flex items-center gap-2 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-[12.5px] font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {state === 'loading' ? <Loader2 class="w-3.5 h-3.5 animate-spin" /> : <Play class="w-3.5 h-3.5" />}
      {state === 'loading' ? 'Loading…' : 'Play recording'}
    </button>
  )
}
